import { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { routeConstant } from "shared/routes/routeConstant";
import {
  ProjectSelectionService,
  ProjectDetailService,
} from "shared/services/project";
import { handleErrors } from "shared/utils/helper";
import {
  DesignerUploadRevisionService,
  GetLatestFeedbackService,
  SetRevisionInProgressService,
} from "shared/services/revision";
import { toastMessage } from "shared/components/common/toast";
import { useRevisionUpload } from "../../../../shared/hooks/useRevisionUpload";
import { FileUploadSection } from "../../../../shared/components/designer/uploadRevision/fileUpload";
import { CommentsSection } from "../../../../shared/components/designer/uploadRevision/comments";
import classNames from "classnames";
import styles from "./styles.module.scss";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomButton from "shared/components/common/customButton";
import ConfirmationModal from "shared/modal/confirmation";
import StartWorkingConfirmationModal from "shared/modal/startWorkingConfirmation";
import SkeletonLoader from "shared/components/common/skeleton";
import CustomInput from "shared/components/common/customInput";
import { UploadSortValue } from "shared/components/common/customUpload";
import { Icons } from "assets";

const UploadRevision = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The query param is the durable source: router state is lost on refresh.
  const projectId = searchParams.get("projectId") || location.state?.projectId;
  const { projectTitle } = location.state || {};
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [comments, setComments] = useState("");
  const [internalComments, setInternalComments] = useState("");
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [projectOptions, setProjectOptions] = useState<any[]>([]);
  const [uploadKey, setUploadKey] = useState(0);
  const [mediaSort, setMediaSort] = useState<UploadSortValue | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [latestRevisionId, setLatestRevisionId] = useState<number | null>(null);
  const [confirmStartWorking, setConfirmStartWorking] = useState(false);
  const [gate, setGate] = useState<
    "allowed" | "needs_start" | "waiting_client" | "wrong_phase"
  >("allowed");
  const [startingWork, setStartingWork] = useState(false);

  const {
    uploadedFileIds,
    uploadingFiles,
    uploadProgress,
    currentFiles,
    fileIdMap,
    processingFilesRef,
    getFileKey,
    handleFilesSelected,
    setUploadedFileIds,
    setCurrentFiles,
    setFileIdMap,
    resetUploadState,
  } = useRevisionUpload();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await ProjectSelectionService(true, true);
      const projects = response?.data?.data || [];

      const formattedProjects = projects.map((project: any) => ({
        id: project.project_id,
        title: project.name,
        ...project,
      }));

      setProjectOptions(formattedProjects);
    } catch (error: any) {
      handleErrors(error);
      setProjectOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Revisions are uploaded from within a project, so the target is fixed by the
  // caller. Without one there is nothing safe to default to.
  useEffect(() => {
    if (loading) return;
    if (!projectId) {
      toastMessage(
        "error",
        "Open a project and use its Upload Revision button to submit a revision.",
      );
      navigate(routeConstant.designer.allProjects.path, { replace: true });
      return;
    }
    const preSelectedProject = projectOptions.find(
      (p) => String(p.id) === String(projectId),
    );

    // Fall back to the router state when the selection list is unavailable; the
    // id must stay numeric because a query param arrives as a string.
    setSelectedProject(
      preSelectedProject || { id: Number(projectId), title: projectTitle || "" },
    );
  }, [projectId, projectTitle, projectOptions, loading, navigate]);

  // Resolve the upload "gate" for the selected project by inspecting its
  // latest revision. in_review => designer must Start Working first;
  // waiting_for_client_response => client hasn't responded yet (can't upload);
  // everything else (incl. no revision / in_progress / completed) => allowed.
  useEffect(() => {
    const currentProjectId = selectedProject?.id;
    if (!currentProjectId) {
      setGate("allowed");
      setLatestRevisionId(null);
      setStatusLoading(false);
      return;
    }

    let cancelled = false;

    const resolveGate = async () => {
      setStatusLoading(true);
      try {
        // Revisions can only be uploaded once the project reaches Preliminary.
        let phaseAllowsUpload = true;
        try {
          const detailResponse = await ProjectDetailService(currentProjectId);
          const projectStatus = (detailResponse?.data?.data?.status || "")
            .toLowerCase()
            .replace(/[-\s]/g, "_");
          phaseAllowsUpload =
            projectStatus.includes("preliminary") ||
            projectStatus.includes("final_review") ||
            projectStatus.includes("completed");
        } catch (phaseError: any) {
          // Can't resolve the phase — let the server decide at submit time.
          phaseAllowsUpload = true;
        }
        if (cancelled) return;

        if (!phaseAllowsUpload) {
          setGate("wrong_phase");
          setLatestRevisionId(null);
          toastMessage(
            "error",
            "You can upload a revision once this project reaches the Preliminary phase.",
          );
          return;
        }

        const response = await GetLatestFeedbackService(currentProjectId);
        if (cancelled) return;

        const data = response?.data?.data || response?.data;
        const normalizedStatus = (data?.revision_status || "")
          .toLowerCase()
          .replace(/[-\s]/g, "_");

        setLatestRevisionId(data?.revision_id ?? null);

        if (normalizedStatus === "in_review") {
          setGate("needs_start");
          toastMessage(
            "error",
            "Please click 'Start Working' before uploading a revision for this project.",
          );
        } else if (normalizedStatus === "waiting_for_client_response") {
          setGate("waiting_client");
          toastMessage(
            "error",
            "This project is waiting for the client's response — you can't upload a revision yet.",
          );
        } else {
          setGate("allowed");
        }
      } catch (error: any) {
        if (cancelled) return;
        // 404 = project has no revisions yet => upload allowed (server permits).
        // Any other error => allow; the server stays the source of truth on submit.
        if (error?.response?.status !== 404) {
          handleErrors(error);
        }
        setGate("allowed");
        setLatestRevisionId(null);
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };

    resolveGate();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolve only when the selected project id changes
  }, [selectedProject?.id]);

  const handleStartWorking = async () => {
    if (!latestRevisionId) return;
    setConfirmStartWorking(false);
    try {
      setStartingWork(true);
      const response = await SetRevisionInProgressService(latestRevisionId);
      if (response.status === 200 || response.status === 201) {
        toastMessage("success", "Revision marked as in progress!");
        setGate("allowed");
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setStartingWork(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedProject?.id) {
      toastMessage("error", "Please select a project");
      return;
    }

    if (!title.trim()) {
      toastMessage("error", "Please enter a revision title");
      return;
    }

    if (!showUploadSuccess) {
      toastMessage("error", "Please upload at least one file");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        title: title.trim(),
        designer_comment: comments.trim() || "",
        internal_comment: internalComments.trim() || "",
        project_id: selectedProject.id,
        media_sort: mediaSort,
        designer_uploads: uploadedFileIds.map((id, index) => ({
          id: id,
          order: index + 1,
        })),
      };

      const response = await DesignerUploadRevisionService(payload);

      if (response.status === 200 || response.status === 201) {
        setShowSubmitModal(true);
        setTitle("");
        setComments("");
        setInternalComments("");
        setMediaSort(null);
        resetUploadState();
        setShowUploadSuccess(false);
        setUploadKey((prev) => prev + 1);
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={classNames(
        styles.pageWrapper,
        "flex flex-col gap-5 w-full h-full",
      )}
    >
      <CustomPageHeading
        title="Upload New Revision"
        description="Upload design files, renderings, and documents for client review"
      />
      <div
        className={classNames(styles.contentContainer, "flex flex-col gap-6")}
      >
        <div className="w-full">
          {loading ? (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-[#212529]">
                Project <span className="text-[#005fd8]">*</span>
              </label>
              <SkeletonLoader className={styles.projectSelectSkeleton} />
            </div>
          ) : (
            <CustomInput
              label="Project"
              value={selectedProject?.title || ""}
              readOnly={true}
              customInputContainer={styles.inputContainer}
              customInputStyle={styles.inputStyle}
              required
            />
          )}
        </div>

        <div className="w-full">
          <CustomInput
            label="Revision Title"
            placeholder="Enter revision title (e.g., Initial Design Concepts)"
            value={title}
            customInputContainer={styles.inputContainer}
            customInputStyle={styles.inputStyle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
            required
          />
        </div>

        <FileUploadSection
          uploadKey={uploadKey}
          uploadingFiles={uploadingFiles}
          uploadProgress={uploadProgress}
          showUploadSuccess={showUploadSuccess}
          currentFiles={currentFiles}
          fileIdMap={fileIdMap}
          uploadedFileIds={uploadedFileIds}
          processingFilesRef={processingFilesRef}
          getFileKey={getFileKey}
          handleFilesSelected={handleFilesSelected}
          setUploadedFileIds={setUploadedFileIds}
          setCurrentFiles={setCurrentFiles}
          setFileIdMap={setFileIdMap}
          setShowUploadSuccess={setShowUploadSuccess}
          mediaSort={mediaSort}
          onMediaSortChange={setMediaSort}
        />

        <CommentsSection
          comments={comments}
          internalComments={internalComments}
          setComments={setComments}
          setInternalComments={setInternalComments}
        />

        <div className="w-full flex justify-end items-center gap-3">
          {gate === "needs_start" && (
            <CustomButton
              Icon={Icons.Rocket}
              IconDirection="left"
              title={startingWork ? "Starting..." : "Start Working"}
              onClick={() => setConfirmStartWorking(true)}
              containerStyle={styles.submitButton}
              loading={startingWork}
              disabled={startingWork || !latestRevisionId}
            />
          )}
          <CustomButton
            title="Upload & Submit to Client"
            onClick={() => setShowSubmitConfirmModal(true)}
            containerStyle={styles.submitButton}
            loading={submitting}
            disabled={
              submitting ||
              !selectedProject ||
              !title.trim() ||
              !showUploadSuccess ||
              statusLoading ||
              gate !== "allowed"
            }
          />
        </div>
      </div>
      <ConfirmationModal
        show={showSubmitConfirmModal}
        modalType="confirmation"
        title="Submit Revision to Client?"
        subtitle="This will submit the revision to the client for review and notify them by email. Please make sure your files and notes are correct."
        actionButtonText="Submit"
        cancelButtonText="Cancel"
        loading={submitting}
        handleClose={() => {
          if (!submitting) setShowSubmitConfirmModal(false);
        }}
        handleSubmit={async () => {
          await handleSubmit();
          setShowSubmitConfirmModal(false);
        }}
      />
      <ConfirmationModal
        show={showSubmitModal}
        modalType="success"
        handleClose={() => {
          setShowSubmitModal(false);
          navigate(routeConstant.designer.home.path);
        }}
        title="Revision Submitted Successfully!"
        subtitle="Client has been notified via email and can now review your changes."
        actionButtonText=""
        cancelButtonText="Got it!"
      />
      <StartWorkingConfirmationModal
        show={confirmStartWorking}
        handleClose={() => setConfirmStartWorking(false)}
        handleSubmit={handleStartWorking}
        loading={startingWork}
      />
    </div>
  );
};

export default UploadRevision;
